import { useContext, useMemo, useState } from 'react';
import { Download } from 'react-feather';
import styled from '@emotion/styled';
import { useFloating, useHover, useInteractions } from '@floating-ui/react';
import type { BarChart, Chart, ChartData, PieChart } from '@trinity/chat';
import { BrandColorCategory, ColorPaletteType, ReportFormat  } from '@trinity/chat';
import { PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context';
import { BrandThemingContext } from '@trinity/chat-components/containers/Branding/Context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useAsyncMutationDownloader } from '@trinity/chat-components/containers/QueryDownloader/index.js';
import { ChartGroupByContext, ChartInfoContext, ChartTypeContext } from './context';
import { BaseMessageButton } from './Message.Button';
import { getGroupedBarChartData, getGroupedPieChartData } from './Message.Response.Chart.utils';

const useDownloadChartImage = (chartData: Chart, title: string, colors: string[]) => {
  const apiClient = useChatApiClient();

  return useAsyncMutationDownloader({
    mutationFn: () => {
      return apiClient.chatReports.downloadChartImage({
        chartData,
        title,
        colors,
      });
    },
  });
};

const useDownloadChartPptx = (chartData: Chart, title: string, colors: string[]) => {
  const apiClient = useChatApiClient();

  return useAsyncMutationDownloader({
    mutationFn: () => {
      return apiClient.chatReports.downloadChartPptx({
        chartData,
        title,
        colors,
      });
    },
  });
};

const useDownloadChartCsv = (quantItemId: number, title: string) => {
  const apiClient = useChatApiClient();

  return useAsyncMutationDownloader({
    mutationFn: () => {
      return apiClient.chatReports.downloadChartCsv({
        quantItemId,
        // title,
      });
    },
  });
}

type Props = {
  className?: string;
  label?: string;
};

export const DownloadChartsButton = ({ className, label = 'Download' }: Props) => {
  const [chart] = useContext(ChartInfoContext);
  const [groupBy] = useContext(ChartGroupByContext);
  const [chartType] = useContext(ChartTypeContext);
  const colors = useChartDownloadColors();

  const exportChartData = useMemo(() => {
    let exportChart: ChartData;

    switch (chartType) {
      case 'stacked-bar': {
        const newData = getGroupedBarChartData(chart.chart.chart.data as BarChart['data'], groupBy);
        exportChart = {
          ...chart,
          chart: {
            ...chart.chart,
            chart: {
              ...(chart.chart.chart as BarChart),
              data: newData,
            },
          },
        };
        break;
      }
      case 'bar': {
        const newData = getGroupedBarChartData(chart.chart.chart.data as BarChart['data'], groupBy);
        exportChart = {
          ...chart,
          chart: {
            ...chart.chart,
            chart: {
              ...(chart.chart.chart as BarChart),
              data: newData,
            },
          },
        };
        break;
      }
      case 'pie': {
        const newData = getGroupedPieChartData(chart.chart.chart.data as PieChart['data'], groupBy);
        exportChart = {
          ...chart,
          chart: {
            ...chart.chart,
            chart: {
              ...(chart.chart.chart as PieChart),
              data: newData,
            },
          },
        };
        break;
      }
      default:
        exportChart = chart;
        break;
    }
    return exportChart;
  }, []);

  const [isOpen, setIsOpen] = useState(false);

  const { context, refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  const hover = useHover(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <div
      ref={refs.setReference}
      {...getReferenceProps()}>
      <BaseMessageButton
        className={className}>
        <StyledIcon size={16} />
        <ButtonCopy>{label}</ButtonCopy>
      </BaseMessageButton>

      {isOpen && (
        <PopperMenu
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}>
          <ExportImgMenuItem
            chart={exportChartData.chart}
            colors={colors}
            title={chart.title}
          />
          <ExportPptMenuItem
            chart={exportChartData.chart}
            colors={colors}
            title={chart.title}
          />
          {chart.quantItemId && (
            <ExportCsvMenuItem
              title={chart.title}
              quantItemId={chart.quantItemId}
            />
          )}
        </PopperMenu>
      )}
    </div>
  );
};

const useChartDownloadColors = () => {
  const branding = useContext(BrandThemingContext);
  const theming = useContext(AppThemingContext);

  return useMemo(() => {
    if (theming.query.data?.enabled) {
      const paletteId = branding.query.data.theming.themes.find(
        (x) => x.categoryId === BrandColorCategory.Options
      )?.paletteId;
      return branding.query.data.theming.palettes.find(
        (x) => x.id === paletteId
      )?.data?.colors;
    }

    return branding.query.data.theming.palettes.find(
      (x) =>
        x.typeId === ColorPaletteType.Preset &&
        x.categoryId === BrandColorCategory.Options
    )?.data?.colors;
  }, [
    theming.query.data?.enabled,
    branding.query.data.theming.palettes,
    branding.query.data.theming.themes,
  ]);
};

type ExportChartProps = {
  chart: Chart;
  colors: ReturnType<typeof useChartDownloadColors>;
  title: string;
};

const ExportImgMenuItem = ({ chart, colors, title }: ExportChartProps) => {
  const downloadImageMutation = useDownloadChartImage(chart, title, colors);
  const { mutate: downloadImage } = downloadImageMutation;

  const handleImageDownload = () => {
    downloadImage({
      meta: {
        format: ReportFormat.Img,
        name: title,
      },
    });
  };

  return (
    <PopperMenuItem
      key="save_img"
      onClick={handleImageDownload}>
      Save as Image
    </PopperMenuItem>
  );
};

const ExportCsvMenuItem = ({ title, quantItemId }: { title: string; quantItemId: number }) => {
  const downloadCsvMutation = useDownloadChartCsv(quantItemId, title);
  const { mutate: downloadCsv } = downloadCsvMutation;

  const handleCsvDownload = () => {
    downloadCsv({
      meta: {
        format: ReportFormat.Csv,
        name: title,
      },
    });
  };

  return (
    <PopperMenuItem
      key="export_csv"
      onClick={handleCsvDownload}>
      Export as CSV
    </PopperMenuItem>
  );
};

const ExportPptMenuItem = ({ chart, colors, title }: ExportChartProps) => {
  const downloadPptxMutation = useDownloadChartPptx(chart, title, colors);
  const { mutate: downloadPptx } = downloadPptxMutation;

  const handlePptxDownload = () => {
    downloadPptx({
      meta: {
        format: ReportFormat.PowerPoint,
        name: title,
      },
    });
  };

  return (
    <PopperMenuItem
      key="export_ppt"
      onClick={handlePptxDownload}>
      Export as PPT
    </PopperMenuItem>
  );
};

const PopperMenu = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  padding: 8,
  borderRadius: 4,
  boxShadow: `0 2px 6px 2px rgba(60, 64, 67, .15)`,
  zIndex: 4,
  textAlign: 'left',
}));

const ButtonCopy = styled.div({
  marginLeft: 5,
});

const StyledIcon = styled(Download)(({ theme }) => ({
  fontSize: 18,
}));